import { useEffect, useState } from "react";
import { Armchair, Eraser, DoorOpen, Bath, LifeBuoy, UserRound, Hash, Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PlanCell } from "@/components/SeatMap";
import { CELL_LABEL, planFromLayout, planFromSeats, renumber, resize, seatCount, setCell, setNumber } from "@/lib/seatplan";

const TOOLS = [["seat", Armchair], ["aisle", Eraser], ["door", DoorOpen], ["wc", Bath], ["driver", LifeBuoy], ["staff", UserRound], ["number", Hash]];
const pill = (on) => `chip btn-press border px-3 py-1.5 ${on ? "border-[#0052FF] bg-[#0052FF] text-white" : "border-slate-200 bg-white text-slate-600"}`;

function Stepper({ label, value, min, max, onChange, testid }) {
  return (
    <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
      {label}
      <Button type="button" size="icon" variant="outline" className="h-7 w-7" disabled={value <= min} onClick={() => onChange(value - 1)} data-testid={`${testid}-minus`}><Minus className="h-3 w-3" /></Button>
      <span className="w-6 text-center font-mono text-sm text-slate-900" data-testid={`${testid}-value`}>{value}</span>
      <Button type="button" size="icon" variant="outline" className="h-7 w-7" disabled={value >= max} onClick={() => onChange(value + 1)} data-testid={`${testid}-plus`}><Plus className="h-3 w-3" /></Button>
    </div>
  );
}

function SeatsBox({ plan, onApply }) {
  const count = seatCount(plan);
  const [n, setN] = useState(count);
  useEffect(() => { setN(count); }, [count]);
  const ok = Number.isInteger(n) && n >= 4 && n <= 70;
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-600">
      Total seats
      <Input type="number" value={n} onChange={(e) => setN(Number(e.target.value))} className="h-8 w-20 bg-white font-mono text-sm text-slate-900" data-testid="plan-total-seats-input" />
      {n !== count && <Button type="button" size="sm" variant="outline" className="h-8" disabled={!ok} onClick={() => onApply(planFromSeats(n, plan.cols))} data-testid="plan-total-seats-apply">Rebuild with {n} seats</Button>}
    </div>
  );
}

function NumberBox({ plan, pick, onSave, onCancel }) {
  const [n, setN] = useState(String(plan.cells[pick.r][pick.i].n));
  const max = seatCount(plan); const v = Number(n);
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-[#0052FF]/30 bg-blue-50 p-2 text-xs font-semibold" data-testid="plan-number-box">
      Seat number (1-{max})
      <Input type="number" value={n} onChange={(e) => setN(e.target.value)} className="h-8 w-20 bg-white" data-testid="plan-number-input" autoFocus />
      <Button type="button" size="sm" className="h-8 bg-[#0052FF]" disabled={!Number.isInteger(v) || v < 1 || v > max} onClick={() => onSave(v)} data-testid="plan-number-save">Set</Button>
      <Button type="button" size="sm" variant="ghost" className="h-8" onClick={onCancel}>Cancel</Button>
      <span className="text-slate-500">The seat already using that number swaps with this one.</span>
    </div>
  );
}

export default function SeatPlanEditor({ value, onChange }) {
  const [tool, setTool] = useState("seat");
  const [painting, setPainting] = useState(false);
  const [pick, setPick] = useState(null);
  useEffect(() => { if (!value) onChange(planFromLayout(45, "2+2")); }, [value, onChange]);
  useEffect(() => { const up = () => setPainting(false); window.addEventListener("pointerup", up); return () => window.removeEventListener("pointerup", up); }, []);
  if (!value) return null;
  const plan = value;
  const paint = (r, i) => {
    if (tool !== "number") return onChange(setCell(plan, r, i, tool));
    if (plan.cells[r][i].t === "seat") setPick({ r, i });
    return null;
  };
  return (
    <div className="space-y-3" data-testid="seat-plan-editor">
      <div className="flex flex-wrap items-center gap-4 rounded-xl bg-slate-50 p-2">
        <SeatsBox plan={plan} onApply={(p) => { setPick(null); onChange(p); }} />
        <Stepper label="Rows" value={plan.rows} min={1} max={20} onChange={(n) => onChange(resize(plan, n, plan.cols))} testid="plan-rows" />
        <Stepper label="Columns" value={plan.cols} min={2} max={6} onChange={(n) => onChange(resize(plan, plan.rows, n))} testid="plan-cols" />
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-600">
        Numbering
        <button type="button" className={pill(plan.numbering === "ltr")} onClick={() => onChange(renumber(plan, "ltr"))} data-testid="plan-number-ltr">Left → right</button>
        <button type="button" className={pill(plan.numbering === "rtl")} onClick={() => onChange(renumber(plan, "rtl"))} data-testid="plan-number-rtl">Right → left</button>
        {plan.numbering === "manual" && <span className="chip bg-amber-50 text-amber-700" data-testid="plan-number-manual">Manual numbers</span>}
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid="plan-tools">
        {TOOLS.map(([k, Ico]) => <button key={k} type="button" className={pill(tool === k)} onClick={() => { setTool(k); setPick(null); }} data-testid={`plan-tool-${k}`}><Ico className="h-3.5 w-3.5" />{k === "number" ? "Set number" : CELL_LABEL[k]}</button>)}
      </div>
      <p className="text-xs text-slate-500">{tool === "number" ? "Tap a seat to type its number." : `Tap or drag across squares to draw: ${CELL_LABEL[tool]}.`} Row 1 is the front of the bus.</p>
      {pick && <NumberBox key={`${pick.r}-${pick.i}`} plan={plan} pick={pick} onSave={(n) => { onChange(setNumber(plan, pick.r, pick.i, n)); setPick(null); }} onCancel={() => setPick(null)} />}
      <div className="overflow-x-auto">
        <div className="inline-block touch-none select-none rounded-[24px] border-2 border-slate-200 bg-white p-3">
          <div className="mb-2 text-center text-[10px] font-bold uppercase tracking-widest text-slate-400">Front</div>
          <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${plan.cols}, 40px)` }} data-testid="plan-grid">
            {plan.cells.flatMap((row, r) => row.map((c, i) => (
              <div key={`${r}-${i}`} onPointerDown={(e) => { e.preventDefault(); setPainting(true); paint(r, i); }} onPointerEnter={() => painting && tool !== "number" && paint(r, i)}
                className={`cursor-crosshair rounded-lg ${c.t === "aisle" ? "border border-dashed border-slate-200" : ""} ${pick?.r === r && pick?.i === i ? "ring-2 ring-[#0052FF]" : ""}`} data-testid={`plan-cell-${r}-${i}`}>
                <PlanCell c={c} size={40} />
              </div>
            )))}
          </div>
        </div>
      </div>
      <div className="text-sm font-bold text-[#182678]" data-testid="plan-seat-count">{seatCount(plan)} passenger seats</div>
    </div>
  );
}
